import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Input, Modal, Radio, Select, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import { BellOutlined, ReloadOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../components/NavigationGuard';
import { formatDateTime } from '../utils/recordView';
import api from '../utils/api';
const quiet = { silentError: true };
const defaultTemplate = '【报名处理提醒】\n类型：{{类型}}\n来源群：{{来源群}}\n姓名：{{姓名}}\n原因：{{原因}}\n记录编号：{{记录编号}}\n时间：{{时间}}\n请登录后台查看并处理。';
export default function FailureAlertSettings() {
  const { user } = useAuth();
  const admin = user?.role === 'super_admin';
  const [form] = Form.useForm();
  const enabled = Form.useWatch('enabled', form);
  const targetMode = Form.useWatch('targetMode', form);
  const fixedGroup = targetMode === 'fixed';
  const [loading, setLoading] = useState(true), [ready, setReady] = useState(false);
  const [robots, setRobots] = useState([]), [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(''), [error, setError] = useState(''), [result, setResult] = useState(null);
  const [dirty, setDirty] = useState(false), [reload, setReload] = useState(0);
  useUnsavedChanges(dirty);
  const loadHistory = () => api.get('/failure-alerts/history', quiet).then(({ data }) => setHistory(data.alerts));
  useEffect(() => {
    if (!admin) { setLoading(false); return; }
    let active = true;
    setLoading(true); setError(''); setReady(false);
    Promise.all([api.get('/failure-alerts', quiet), api.get('/failure-alerts/robots', quiet), api.get('/failure-alerts/history', quiet)])
      .then(([settings, choices, logs]) => {
        if (!active) return;
        form.setFieldsValue({ targetMode: 'source', ...settings.data }); setRobots(choices.data.robots); setHistory(logs.data.alerts); setDirty(false); setReady(true);
      }).catch(err => { if (active) setError(err.response?.data?.message || '提醒配置加载失败，请重试'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [admin, form, reload]);
  if (!admin) return <Alert type="warning" showIcon message="推送反馈配置需要超级管理员权限" />;
  const perform = async action => {
    try {
      const values = await form.validateFields();
      if (action === 'test' && (!values.senderRobotId || !values.groupName)) {
        setError('请先选择发送机器人并填写测试群名称'); return;
      }
      const run = async () => {
        setBusy(action); setError(''); setResult(null);
        try {
          if (action === 'save') {
            const { data } = await api.put('/failure-alerts', values, quiet);
            form.setFieldsValue(data); setDirty(false); message.success('推送反馈配置已保存并立即生效');
          } else {
            const { data } = await api.post('/failure-alerts/test', values, quiet);
            setResult({ type: 'success', message: data.message });
          }
        } catch (err) { setResult({ type: 'error', message: err.response?.data?.message || '操作失败，请稍后重试' }); }
        finally { setBusy(''); loadHistory().catch(() => {}); }
      };
      if (action === 'test') Modal.confirm({ title: '发送测试提醒', content: `将使用当前填写的参数，向“${values.groupName}”发送一条成功反馈示例，不会保存配置。`, okText: '发送测试提醒', cancelText: '取消', onOk: run });
      else await run();
    } catch { /* 表单会显示具体字段错误。 */ }
  };
  const statuses = { pending: ['处理中 / 待核对', 'blue'], accepted: ['接口已受理', 'green'], failed: ['未发送', 'red'], unknown: ['接收结果待确认', 'orange'] };
  return <div className="failure-alert-settings">
    <div className="page-header"><Typography.Title level={2}>推送反馈</Typography.Title>
      <div className="description">每条报名向伙伴通提交后，默认向接收并处理该消息的来源群发送简短的成功或失败反馈。</div></div>
    {error && <Alert className="page-alert" type="error" showIcon message={error} action={!ready && <Button onClick={() => setReload(v => v + 1)}>重试</Button>} />}
    <Spin spinning={loading}><Card title={<Space><BellOutlined />提醒规则{dirty && <Tag color="orange">有未保存的修改</Tag>}</Space>}>
      <Alert className="page-alert" type="info" showIcon message="提醒与报名提交独立处理"
        description="伙伴通已接收后发送成功反馈；超时显示结果待确认。每条报名的每次提交只反馈一次，默认回到原报名群，手动推送同样适用。也可选择指定群集中接收。目标群需已加入所选机器人的微信，群名需完整且唯一。反馈发送失败不改变报名结果。" />
      {!loading && !robots.length && <Alert className="page-alert" type="warning" showIcon message="暂无可用的发送机器人，请先到系统设置 → 机器人管理添加，功能类型选择“发送”或“双向”。" />}
      <Form name="failure_alerts" form={form} layout="vertical" disabled={loading || !ready || Boolean(busy)}
        initialValues={{ enabled: false, targetMode: 'source', notifyExtraction: true, notifySubmission: true, template: defaultTemplate }} onFinish={() => perform('save')}
        onValuesChange={() => { setDirty(true); setResult(null); }}>
        <Form.Item name="enabled" label="启用推送反馈" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="senderRobotId" label="发送机器人" rules={[{ required: Boolean(enabled), message: '请选择发送机器人' }]}>
          <Select showSearch optionFilterProp="label" placeholder="选择已启用的发送机器人" options={robots.map(robot => ({ value: robot.worktoolRobotId, label: `${robot.name}（${robot.worktoolRobotId}）` }))} />
        </Form.Item>
        <Form.Item name="targetMode" label="反馈接收群">
          <Radio.Group><Radio value="source">消息来源群（默认）</Radio><Radio value="fixed">指定群</Radio></Radio.Group>
        </Form.Item>
        <Form.Item name="groupName" label={fixedGroup ? '提醒群名称' : '测试群名称'} rules={[{ required: Boolean(enabled) && fixedGroup, whitespace: true, message: '请输入完整的提醒群名称' }]}
          extra={fixedGroup ? '所有反馈发送到此群，请填写完整且唯一的群名。' : '可留空，正式反馈按每条消息的来源群发送；此群名仅用于发送测试提醒。'}>
          <Input maxLength={200} placeholder="请输入完整群名" />
        </Form.Item>
        <Form.Item label="提醒类型" required={Boolean(enabled)}><Space wrap>
          <Form.Item name="notifyExtraction" valuePropName="checked" noStyle><Checkbox>人员提取失败</Checkbox></Form.Item>
          <Form.Item name="notifySubmission" valuePropName="checked" noStyle><Checkbox>推送结果（成功、失败及待确认）</Checkbox></Form.Item>
        </Space></Form.Item>
        <Form.Item label="推送反馈格式">
          <Typography.Paragraph>成功：✅自动推送成功 + 姓名 + 岗位</Typography.Paragraph>
          <Typography.Paragraph>失败：❌自动推送失败 + 姓名 + 岗位 + 失败原因</Typography.Paragraph>
          <Typography.Text type="secondary">消息按行显示。手动提交对应显示“手动推送”；未匹配到岗位时显示原文岗位或站点。</Typography.Text>
        </Form.Item>
        <Form.Item name="template" label="提取失败提醒模板" rules={[{ required: true, whitespace: true, message: '请输入提醒消息模板' }]}
          extra="可用变量：{{类型}}、{{来源群}}、{{姓名}}、{{原因}}、{{记录编号}}、{{时间}}。也可以直接填写固定提醒文字。">
          <Input.TextArea rows={8} maxLength={1200} showCount />
        </Form.Item>
        <Space wrap><Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={busy === 'save'}>保存配置</Button>
          <Button icon={<SendOutlined />} loading={busy === 'test'} onClick={() => perform('test')}>发送测试提醒</Button></Space>
      </Form>
      {result && <Alert style={{ marginTop: 20 }} showIcon {...result} />}
    </Card></Spin>
    <Card className="section-card" title="最近 20 条提醒" extra={<Button icon={<ReloadOutlined />} disabled={!ready || Boolean(busy)} onClick={() => loadHistory().catch(() => message.error('提醒记录加载失败，请重试'))}>刷新</Button>}>
      <Table rowKey="_id" dataSource={history} pagination={false} columns={[
        { title: '类型 / 时间', key: 'kind', width: 185, render: (_, record) => <div>{({ extraction: '人员提取失败', submission: '推送异常', submission_success: '推送成功', test: '测试反馈' })[record.kind]}<div className="cell-secondary">{formatDateTime(record.createdAt)}</div></div> },
        { title: '提醒群', dataIndex: 'groupName', width: 170 },
        { title: '状态', dataIndex: 'status', width: 165, render: status => <Tag color={statuses[status]?.[1]}>{statuses[status]?.[0] || status}</Tag> },
        { title: '消息 / 结果', key: 'message', width: 360, render: (_, record) => <div className="alert-message-preview">{record.error || record.message}</div> }
      ]} scroll={{ x: 880 }} />
    </Card>
  </div>;
}
